import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, X, ChevronLeft, ChevronRight } from "lucide-react";
import { CASE_STUDIES } from "@/data/site";
import { useLead } from "@/App";
import { MaskedLines, FadeUp, EASE } from "./motion";

export default function CaseStudy() {
  const { slug } = useParams();
  const cs = CASE_STUDIES[slug];
  const { openConsult } = useLead();
  const [active, setActive] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    window.__lenis?.scrollTo(0, { immediate: true });
  }, [slug]);

  if (!cs) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#1B1B1B]">
        <Link to="/" data-testid="case-notfound-back" className="font-display text-2xl text-[#F7F5F2]">← Back to home</Link>
      </div>
    );
  }

  const idx = active !== null ? active : -1;
  const nav = (dir) => setActive((idx + dir + cs.gallery.length) % cs.gallery.length);

  return (
    <main data-testid="case-study-page" className="bg-[#F7F5F2]">
      <section className="grain relative flex min-h-[72vh] items-end overflow-hidden bg-[#1B1B1B]">
        <motion.img
          src={cs.cover}
          alt={cs.title}
          className="absolute inset-0 h-full w-full object-cover"
          initial={{ scale: 1.15, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.6, ease: EASE }}
        />
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-16 pt-40 md:px-12">
          <Link to="/" data-testid="case-back-link" className="mb-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#F7F5F2]/70 transition-colors hover:text-[#B68D40]">
            <ArrowLeft size={14} /> All Projects
          </Link>
          <p className="mb-4 font-num text-[11px] uppercase tracking-[0.4em] text-[#B68D40]">{cs.tag}</p>
          <MaskedLines
            className="font-display text-4xl tracking-tight text-[#F7F5F2] md:text-6xl"
            lines={[cs.title]}
            delay={0.2}
          />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.6 }}
            className="mt-8 flex flex-wrap gap-3"
          >
            {cs.facts.map((f) => (
              <span key={f} data-testid={`case-fact-${f.toLowerCase().replace(/[\s.]/g, "-")}`} className="glass-dark rounded-full border border-white/15 px-5 py-2 font-num text-[11px] uppercase tracking-[0.2em] text-[#F7F5F2]">
                {f}
              </span>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-6 py-24 md:px-12 md:py-32">
        {cs.desc.map((p, i) => (
          <FadeUp key={i} delay={i * 0.1}>
            <p className={`${i === 0 ? "font-serif2 text-2xl leading-relaxed text-[#1B1B1B] md:text-3xl" : "mt-8 text-base leading-relaxed text-[#2C2C2C]/70"}`}>
              {p}
            </p>
          </FadeUp>
        ))}
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-24 md:px-12 md:pb-32">
        <FadeUp>
          <h2 className="font-display text-3xl tracking-tight text-[#1B1B1B] md:text-4xl">
            The <span className="italic text-[#B68D40]">gallery</span>
          </h2>
        </FadeUp>
        <div className="mt-12 columns-1 gap-5 sm:columns-2 lg:columns-3">
          {cs.gallery.map((img, i) => (
            <FadeUp key={i} delay={(i % 3) * 0.08} className="mb-5 break-inside-avoid">
              <button
                onClick={() => setActive(i)}
                data-testid={`case-gallery-${i}`}
                data-cursor="View"
                className="group block w-full overflow-hidden rounded-xl"
              >
                <img
                  src={img}
                  alt={`${cs.title} — photo ${i + 1}`}
                  loading="lazy"
                  className="w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
              </button>
            </FadeUp>
          ))}
        </div>
      </section>

      <section className="grain bg-[#1B1B1B] py-24">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-8 px-6 md:px-12">
          <h2 className="max-w-xl font-display text-4xl tracking-tight text-[#F7F5F2] md:text-5xl">
            Want a home <span className="italic text-[#B68D40]">like this?</span>
          </h2>
          <button
            onClick={() => openConsult("case_study_" + slug)}
            data-testid="case-study-cta"
            className="group flex items-center gap-3 rounded-full bg-[#B68D40] px-8 py-4 text-sm font-semibold uppercase tracking-widest text-[#1B1B1B] transition-colors duration-300 hover:bg-[#F7F5F2]"
          >
            Book Free Consultation
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </div>
      </section>

      <AnimatePresence>
        {active !== null && (
          <motion.div
            data-testid="case-lightbox"
            className="fixed inset-0 z-[70] flex items-center justify-center bg-[#1B1B1B]/95 p-4 backdrop-blur-sm md:p-12"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActive(null)}
          >
            <button data-testid="case-lightbox-close" onClick={() => setActive(null)} className="absolute right-6 top-6 z-10 flex h-12 w-12 items-center justify-center rounded-full border border-[#F7F5F2]/25 text-[#F7F5F2] transition-colors hover:bg-[#B68D40] hover:text-[#1B1B1B]" aria-label="Close">
              <X size={20} />
            </button>
            <button data-testid="case-lightbox-prev" onClick={(e) => { e.stopPropagation(); nav(-1); }} className="absolute left-4 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-[#F7F5F2]/25 text-[#F7F5F2] transition-colors hover:bg-[#B68D40] hover:text-[#1B1B1B] md:left-8" aria-label="Previous">
              <ChevronLeft size={20} />
            </button>
            <button data-testid="case-lightbox-next" onClick={(e) => { e.stopPropagation(); nav(1); }} className="absolute right-4 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-[#F7F5F2]/25 text-[#F7F5F2] transition-colors hover:bg-[#B68D40] hover:text-[#1B1B1B] md:right-8" aria-label="Next">
              <ChevronRight size={20} />
            </button>
            <motion.figure
              key={active}
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, ease: EASE }}
              className="max-h-full max-w-5xl"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={cs.gallery[active]} alt={`${cs.title} — photo ${active + 1}`} className="max-h-[80vh] w-full rounded-lg object-cover" />
              <figcaption className="mt-4 text-center font-num text-xs text-[#F7F5F2]/50">{active + 1} / {cs.gallery.length}</figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
